import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/cn";
import { BLOCK_SURFACE_FRAME } from "./blockSurface";

/**
 * DataTable - sortable, paginated table. Renderer for the K2 `table` block
 * (`docs/contracts/block-registry.md`): the platform's chat `BlockRenderer`
 * passes a `table` block's `props` straight through, so the prop names match
 * the block payload exactly.
 *
 * Block props (`{ columns[], rows[][], sort?, page? }`):
 *   - `columns` - column definitions. Each is `{ key, label, align?, sortable? }`
 *     or a bare string (used as both key + label). Order is the render order;
 *     `rows` cells are positional and index 1:1 against this array.
 *   - `rows` - an array of rows, each row an array of cell values, indexed to
 *     `columns`. Cells are primitives (string / number / boolean / null).
 *   - `sort?` - initial sort: `{ by, direction }` where `by` is a column key
 *     or numeric index and `direction` is `"asc"` | `"desc"`. Sorting is
 *     applied client-side; clicking a sortable header re-sorts.
 *   - `page?` - pagination: `{ size, index? }`. When `size` is set, the table
 *     paginates client-side with prev / next controls. Omit for no paging.
 *
 * Client-only props (never part of the streamed block): `className`,
 * `caption`, `loading`, `error`, `emptyMessage`, `onSortChange`,
 * `onPageChange`.
 *
 * Both `sort` and `page` are treated as *initial* state the widget then owns
 * (so a streamed block renders interactively out of the box); pass
 * `onSortChange` / `onPageChange` to mirror the state back to a controller.
 *
 * Theming is entirely token-driven (`bg-card`, `border-border`,
 * `text-foreground`, `text-muted-foreground`, `bg-muted`, …) - no hardcoded
 * colors. SSR-safe: no `window` / `document` access. Sorting is stable and
 * null-safe; mixed string / number columns compare numerically when both
 * cells are numbers, lexically otherwise.
 */

const dataTableVariants = cva(
  cn("w-full overflow-hidden", BLOCK_SURFACE_FRAME),
);

export type CellValue = string | number | boolean | null;
export type SortDirection = "asc" | "desc";

export interface DataTableColumn {
  /** Stable key for this column. Also matches `sort.by` when keyed. */
  key: string;
  /** Header label. */
  label: string;
  /** Cell + header alignment. Default `"left"`. */
  align?: "left" | "right" | "center";
  /** Whether the header is clickable to sort. Default `true`. */
  sortable?: boolean;
}

/** A column is either a full definition or a bare string (key === label). */
export type DataTableColumnInput = DataTableColumn | string;

export interface DataTableSort {
  /** Column key or zero-based index to sort by. */
  by: string | number;
  /** Sort direction. Default `"asc"`. */
  direction?: SortDirection;
}

export interface DataTablePage {
  /** Rows per page. Required to enable pagination. */
  size: number;
  /** Initial zero-based page index. Default `0`. */
  index?: number;
}

export interface DataTableProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof dataTableVariants> {
  /** Column definitions; `rows` cells index against this array. */
  columns: DataTableColumnInput[];
  /** Rows of positional cell values. */
  rows: CellValue[][];
  /** Initial sort the widget then owns. */
  sort?: DataTableSort;
  /** Pagination config. Omit to render all rows. */
  page?: DataTablePage;
  /** Caption rendered below the table. */
  caption?: React.ReactNode;
  /** Render a loading skeleton instead of content. */
  loading?: boolean;
  /** Render an error message instead of content. Pass `true` or a string. */
  error?: boolean | string;
  /** Message shown when there are no rows. */
  emptyMessage?: string;
  /** Fired when the user re-sorts via a header click. */
  onSortChange?: (sort: DataTableSort) => void;
  /** Fired when the user pages. */
  onPageChange?: (index: number) => void;
}

function normalizeColumns(columns: DataTableColumnInput[]): DataTableColumn[] {
  return columns.map((col) =>
    typeof col === "string"
      ? { key: col, label: col, align: "left", sortable: true }
      : { align: "left", sortable: true, ...col },
  );
}

/** Resolve a `sort.by` (key or index) to a column index, or -1 if unknown. */
function resolveSortIndex(by: string | number, columns: DataTableColumn[]): number {
  if (typeof by === "number") return by >= 0 && by < columns.length ? by : -1;
  return columns.findIndex((c) => c.key === by);
}

const alignClass = (align: DataTableColumn["align"]): string =>
  align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

function compareCells(a: CellValue, b: CellValue): number {
  // Nulls sort last regardless of direction's later flip.
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function renderCell(value: CellValue): React.ReactNode {
  if (value == null) return <span className="text-muted-foreground">-</span>;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export const DataTable = React.forwardRef<HTMLDivElement, DataTableProps>(function DataTable(
  {
    className,
    columns,
    rows,
    sort,
    page,
    caption,
    loading = false,
    error = false,
    emptyMessage = "No rows to show.",
    onSortChange,
    onPageChange,
    ...rest
  },
  ref,
) {
  const normalizedColumns = React.useMemo(() => normalizeColumns(columns), [columns]);

  const [sortState, setSortState] = React.useState<DataTableSort | undefined>(sort);
  const [pageIndex, setPageIndex] = React.useState<number>(page?.index ?? 0);

  // A streamed block can update in place (same block_id, new rows / sort). Keep
  // the widget's owned state in sync when the incoming initial props change.
  React.useEffect(() => {
    setSortState(sort);
  }, [sort]);
  React.useEffect(() => {
    setPageIndex(page?.index ?? 0);
  }, [page?.index]);

  const sortedRows = React.useMemo(() => {
    if (!sortState) return rows;
    const colIndex = resolveSortIndex(sortState.by, normalizedColumns);
    if (colIndex < 0) return rows;
    const dir = sortState.direction === "desc" ? -1 : 1;
    // Stable sort: decorate with original index, sort, undecorate.
    return rows
      .map((row, i) => ({ row, i }))
      .sort((x, y) => {
        const c = compareCells(x.row[colIndex], y.row[colIndex]);
        return c !== 0 ? c * dir : x.i - y.i;
      })
      .map((d) => d.row);
  }, [rows, sortState, normalizedColumns]);

  const pageSize = page?.size;
  const pageCount = pageSize ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const clampedPage = Math.min(pageIndex, pageCount - 1);
  const visibleRows = React.useMemo(() => {
    if (!pageSize) return sortedRows;
    const start = clampedPage * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, pageSize, clampedPage]);

  const handleSort = (col: DataTableColumn) => {
    if (!col.sortable) return;
    const currentIndex = sortState ? resolveSortIndex(sortState.by, normalizedColumns) : -1;
    const thisIndex = normalizedColumns.indexOf(col);
    const nextDirection: SortDirection =
      currentIndex === thisIndex && sortState?.direction !== "desc" ? "desc" : "asc";
    const next: DataTableSort = { by: col.key, direction: nextDirection };
    setSortState(next);
    setPageIndex(0);
    onSortChange?.(next);
  };

  const goToPage = (index: number) => {
    const clamped = Math.max(0, Math.min(index, pageCount - 1));
    setPageIndex(clamped);
    onPageChange?.(clamped);
  };

  if (loading) {
    return (
      <div
        ref={ref}
        className={cn(dataTableVariants(), className)}
        aria-busy="true"
        data-slot="data-table"
        {...rest}
      >
        <div className="space-y-3 p-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-4 w-full animate-pulse rounded bg-muted motion-reduce:animate-none"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    const message = typeof error === "string" ? error : "Couldn't load this table.";
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          dataTableVariants(),
          "border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive",
          className,
        )}
        data-slot="data-table"
        {...rest}
      >
        {message}
      </div>
    );
  }

  const activeSortIndex = sortState ? resolveSortIndex(sortState.by, normalizedColumns) : -1;

  return (
    <div
      ref={ref}
      className={cn(dataTableVariants(), className)}
      data-slot="data-table"
      {...rest}
    >
      <div className="relative w-full overflow-x-auto">
        <table className="w-full caption-bottom text-sm">
          {caption ? (
            <caption className="mt-4 px-5 pb-3 text-sm text-muted-foreground">{caption}</caption>
          ) : null}
          <thead className="[&_tr]:border-b">
            <tr className="border-b">
              {normalizedColumns.map((col, i) => {
                const isActive = i === activeSortIndex;
                const ariaSort = isActive
                  ? sortState?.direction === "desc"
                    ? "descending"
                    : "ascending"
                  : "none";
                return (
                  <th
                    key={col.key}
                    aria-sort={col.sortable ? ariaSort : undefined}
                    className={cn(
                      "h-11 px-4 align-middle font-medium whitespace-nowrap text-foreground first:pl-5 last:pr-5",
                      alignClass(col.align),
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col)}
                        className={cn(
                          "inline-flex items-center gap-1 rounded text-foreground transition-colors hover:text-foreground/80",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          col.align === "right" && "flex-row-reverse",
                        )}
                      >
                        <span>{col.label}</span>
                        {isActive ? (
                          sortState?.direction === "desc" ? (
                            <ChevronDown className="size-3.5 shrink-0 opacity-70" aria-hidden />
                          ) : (
                            <ChevronUp className="size-3.5 shrink-0 opacity-70" aria-hidden />
                          )
                        ) : (
                          <ChevronsUpDown
                            className="size-3.5 shrink-0 opacity-40"
                            aria-hidden
                          />
                        )}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="[&_tr:last-child]:border-0">
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={normalizedColumns.length || 1}
                  className="px-5 py-10 text-center text-sm text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              visibleRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="border-b transition-colors hover:bg-muted/50"
                >
                  {normalizedColumns.map((col, colIndex) => (
                    <td
                      key={col.key}
                      className={cn(
                        "px-4 py-3 align-middle whitespace-nowrap tabular-nums first:pl-5 last:pr-5",
                        alignClass(col.align),
                      )}
                    >
                      {renderCell(row[colIndex] ?? null)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageSize && sortedRows.length > pageSize ? (
        <div className="flex items-center justify-between gap-2 border-t border-border/70 px-5 py-2.5 text-xs text-muted-foreground">
          <span className="tabular-nums">
            Page {clampedPage + 1} of {pageCount}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => goToPage(clampedPage - 1)}
              disabled={clampedPage <= 0}
              className={cn(
                "rounded-md border border-border px-2 py-1 font-medium text-foreground transition-colors",
                "hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => goToPage(clampedPage + 1)}
              disabled={clampedPage >= pageCount - 1}
              className={cn(
                "rounded-md border border-border px-2 py-1 font-medium text-foreground transition-colors",
                "hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
});

DataTable.displayName = "DataTable";

export { dataTableVariants };
export type DataTableVariantProps = VariantProps<typeof dataTableVariants>;
